import { useMemo } from 'react';
import { useQuery, type QueryClient } from '@tanstack/react-query';
import type { CustomEmoji } from '@avslack/shared';
import { listFrequentEmoji } from '../lib/api';
import { findEmoji, type EmojiEntry } from './emoji';
import { useCustomEmoji } from './custom-emoji';

export const frequentEmojiKey = ['emoji-frequent'] as const;

/** A "Gyakori" sor egy eleme: beepitett jel vagy a ceg egyedi emojija. */
export type FrequentEmoji =
  { kind: 'builtin'; entry: EmojiEntry } | { kind: 'custom'; emoji: CustomEmoji };

/**
 * A SAJAT leggyakoribb emojik, a szerver szamolasa szerint.
 *
 * ─── Miert nem frissul minden megnyitaskor? ─────────────────────────────────
 *
 * Mert a valaszto megnyitasakor az ember mar celoz: ha ilyenkor jonne meg egy
 * friss lista, a sor atrendezodne a kurzor alatt, es mellekattintana. Ezert a
 * lista a HASZNALAT UTAN frissul a hatterben ({@link refreshFrequentEmoji}),
 * a kovetkezo megnyitasra mar keszen all.
 *
 * A `rank` a keresesnek szol: a szerkeszto `:sz` javaslatai is a sajat
 * szokas szerint rendezodnek.
 */
export function useFrequentEmoji() {
  const query = useQuery({
    queryKey: frequentEmojiKey,
    queryFn: listFrequentEmoji,
    staleTime: 10 * 60_000,
  });
  const { byName } = useCustomEmoji();

  return useMemo(() => {
    const rank = new Map<string, number>();
    const items: FrequentEmoji[] = [];

    for (const usage of query.data ?? []) {
      rank.set(usage.emoji, usage.score);

      const custom = /^:(.+):$/.exec(usage.emoji);
      if (custom) {
        // A torolt egyedi emoji kimarad: nincs mit kirajzolni.
        const emoji = byName.get(custom[1]!);
        if (emoji) items.push({ kind: 'custom', emoji });
        continue;
      }

      /*
        A keszleten KIVULI jel is marad.

        Aki a telefonja billentyuzetevel kuld 🦄-t, annak az is a szokasa, meg
        ha a mi valogatasunkban nincs is benne. Nev hijan a jel maga a felirat.
      */
      items.push({
        kind: 'builtin',
        entry: findEmoji(usage.emoji) ?? { char: usage.emoji, keywords: [usage.emoji] },
      });
    }

    return { items, rank };
  }, [query.data, byName]);
}

/**
 * Hasznalat utan: a lista frissitese a HATTERBEN, akkor is, ha epp egy
 * valaszto sincs nyitva - a `type: 'all'` az inaktiv lekerdezest is ujrakeri.
 */
export function refreshFrequentEmoji(queryClient: QueryClient): void {
  void queryClient.refetchQueries({ queryKey: frequentEmojiKey, type: 'all' });
}
